import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, makeStyles } from '@material-ui/core';
import { Typography } from '@material-ui/core';
import { useFormikContext } from 'formik';

import { RemoveIcon, ValidatedTextField } from 'components';
import { AssignmentFragment, AssignmentInput } from 'api';

import DefineUsers from './DefineUsers';
import GroupsRequired from '../Pickers/GroupsRequired';
import GroupsSuggested from '../Pickers/GroupsSuggested';
import UsersRequired from '../Pickers/UsersRequired';
import UsersSuggested from '../Pickers/UsersSuggested';

interface Props {
  assignment?: AssignmentFragment;
}

const useStyles = makeStyles(theme => ({
  divider: {
    margin: theme.spacing(2, -5, 2, 0),
  },
  header: {
    margin: theme.spacing(4, 0, 0),
  },
  userCount: {
    fontSize: theme.spacing(2.5),
  },
}));

const AssignmentForm: FC<Props> = ({ assignment }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { values, setFieldValue } = useFormikContext<AssignmentInput>();
  const { groups_required_ids, groups_suggested_ids, users_required_ids, users_suggested_ids } =
    values;

  return (
    <Fragment>
      <ValidatedTextField name="title" i18nKey="admin:assignmentTitle" largeText />

      <DefineUsers />

      <Typography variant="h5" color="textSecondary" className={classes.header}>
        {t('admin:defineAssignments')}
      </Typography>
      <Divider className={classes.divider} />

      <Box pl={4} pt={3} pb={3}>
        <Grid container>
          <Grid item xs={2}>
            <Typography variant="subtitle2">{t('common:group_plural')}</Typography>
          </Grid>
          <Grid item xs={10}>
            <Typography variant="subtitle2">{t('common:required')}</Typography>
            <Typography color="textSecondary">{t('admin:onboardingGroupsRequired')}</Typography>
          </Grid>
        </Grid>
        <Box mb={4} mt={1}>
          <Grid container alignItems="center">
            <Grid item xs={2} />
            <Grid item xs={9}>
              <GroupsRequired groups={assignment?.groups_required_objects} />
            </Grid>
            {!!groups_required_ids?.length && (
              <Grid item xs={1} container justify="center">
                <RemoveIcon onClick={() => setFieldValue('groups_required_ids', [])} />
              </Grid>
            )}
          </Grid>
        </Box>

        <Grid container>
          <Grid item xs={2} />
          <Grid item xs={10}>
            <Typography variant="subtitle2">{t('common:suggested')}</Typography>
          </Grid>
        </Grid>
        <Box mb={6} mt={1}>
          <Grid container alignItems="center">
            <Grid item xs={2} />
            <Grid item xs={9}>
              <GroupsSuggested groups={assignment?.groups_suggested_objects} />
            </Grid>
            {!!groups_suggested_ids?.length && (
              <Grid item xs={1} container justify="center">
                <RemoveIcon onClick={() => setFieldValue('groups_suggested_ids', [])} />
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider className={classes.divider} />

        <Box pt={3}>
          <Grid container>
            <Grid item xs={2}>
              <Typography variant="subtitle2">{t('common:person_plural')}</Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography variant="subtitle2">{t('common:required')}</Typography>
              <Typography color="textSecondary">{t('admin:onboardingUsersRequired')}</Typography>
            </Grid>
          </Grid>
          <Box mb={4} mt={1}>
            <Grid container alignItems="center">
              <Grid item xs={2} />
              <Grid item xs={9}>
                <UsersRequired users={assignment?.users_required_objects} />
              </Grid>
              {!!users_required_ids?.length && (
                <Grid item xs={1} container justify="center">
                  <RemoveIcon onClick={() => setFieldValue('users_required_ids', [])} />
                </Grid>
              )}
            </Grid>
          </Box>

          <Grid container>
            <Grid item xs={2} />
            <Grid item xs={10}>
              <Typography variant="subtitle2">{t('common:suggested')}</Typography>
            </Grid>
          </Grid>
          <Box mb={4} mt={1}>
            <Grid container alignItems="center">
              <Grid item xs={2} />
              <Grid item xs={9}>
                <UsersSuggested users={assignment?.users_suggested_objects} />
              </Grid>
              {!!users_suggested_ids?.length && (
                <Grid item xs={1} container justify="center">
                  <RemoveIcon onClick={() => setFieldValue('users_suggested_ids', [])} />
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      </Box>
    </Fragment>
  );
};

export default AssignmentForm;
